export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    if (timeout) clearTimeout(timeout);

    timeout = setTimeout(() => {
      func.apply(this, args);
    }, wait);
  };
}

// 节流函数 - 限制函数执行频率
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastExecTime = 0;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    const currentTime = Date.now();

    if (currentTime - lastExecTime > wait) {
      func.apply(this, args);
      lastExecTime = currentTime;
    } else {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
        lastExecTime = Date.now();
      }, wait - (currentTime - lastExecTime));
    }
  };
}

function joinPath(part1: string, part2: string) {
  return `${part1?.replace(/\/+$/, '')}/${part2?.replace(/^\/+/, '')}`;
}

// 根据字典换文字
export function getDictTxt(
  value: number | string | (number | string)[] | undefined,
  list: { key: number | string; value: string }[]
) {
  if (value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    const resString: string[] = [];
    value.forEach((val) => {
      const targetItem = list.find((item) => item.key == val);
      if (targetItem) {
        resString.push(targetItem.value);
      }
    });
    return resString.join('，');
  }
  const valueList = list.filter((item) => item.key == value);
  if (valueList.length) {
    return valueList[0].value;
  } else {
    return '';
  }
}

// 所属单位换文字
export function getOrgTxt(
  value: number | string | undefined,
  list: { code: number | string; name: string }[]
) {
  if (value === undefined) {
    return '';
  }
  const valueList = list.filter((item) => item.code == value);
  if (valueList.length) {
    return valueList[0].name;
  } else {
    return '';
  }
}

// 拼接请求路径
export function accessPath(path: string, hasToken = true, domain?: string) {
  if (!path) return '';
  const token = sessionStorage.getItem('token');
  let accessToken = `?access_token=${token}`;
  if (!hasToken) {
    accessToken = '';
  }
  if (domain) {
    return `${joinPath(domain, path)}${accessToken}`;
  } else {
    const defaultDomain = sessionStorage.getItem('ossEndpoint') || '';
    return `${joinPath(defaultDomain, path)}${accessToken}`;
  }
}

// 在函数外部或文件顶部定义接口
interface AccessItem {
  resKey: string;
  [key: string]: any; // 如果还有其他属性
}

export function accessControl(resKey: string) {
  const accessListStr = sessionStorage.getItem('userAuth');
  const accessList: AccessItem[] = accessListStr ? JSON.parse(accessListStr).appResInfos : [];
  const findIndex = accessList.findIndex((item) => item.resKey == resKey);
  if (findIndex == -1) {
    return false;
  }
  return true;
}

// 在列表查找是否有权限单项,并返回
export const filterAuthorizedItem = <
  T extends {
    auth?: string;
    [key: string]: any;
  }
>(
  items: T[]
): T | null => {
  return items.find((item) => !item.auth || (item.auth && accessControl(item.auth))) || null;
};

// 图表字体大小
export function setChartFontSize(size: number): number {
  const pxEachRem = sessionStorage.getItem('pxEachRem');
  return pxEachRem ? size * Number(pxEachRem) : size;
}

// 空显示默认值
export const setNullValue = (val: number | string, formatVal?: string | number) => {
  formatVal = formatVal ?? '-';
  return val === undefined || val === null || val === '' ? formatVal : val;
};

/**
 * 手机号格式校验
 * 只对国内（中国、中国澳门、中国台湾、中国香港）手机号进行强制校验，国外的只校验输入是否为数字
 */
interface VerifyPhoneNumberParams {
  countryCode: number;
  phoneNumber: string;
}
export const verifyPhoneNumber = (param: VerifyPhoneNumberParams) => {
  const { countryCode, phoneNumber } = param;
  if (!countryCode || !phoneNumber) return false;
  const verifyRules: Record<number, RegExp> = {
    86: /^1\d{10}$/, // 中国大陆：开头1 3,4,5,7,8号段，后边跟9位数字
    853: /^[6]([8|6])\d{5}$/, // 澳门：66或68开头后面跟5位数字
    886: /^[0][9]\d{8}$/, // 台湾：09开头后面跟8位数字
    852: /^([6|9])\d{7}$/, // 香港：9或6开头后面跟7位数字
  };
  if (verifyRules[countryCode]) {
    return verifyRules[countryCode].test(phoneNumber);
  } else {
    return /^[0-9]+$/.test(phoneNumber);
  }
};
